import { Auth } from "aws-amplify";
import { Component, FunctionComponent } from "react";
import { RouteComponentProps } from "react-router-dom";
import { IGetProducts, ISyncProducts, Product } from "../../client/core";
import { Color, Storage } from "../../env";
import { Accordion, Badge, Col, ListGroup, Row, Table } from "react-bootstrap";
import { List } from "react-bootstrap-icons";
import { Button, variant } from "../form/Button";

export interface ZettleDashboardState {
    isLoading: boolean,
    error: string
    zettleProducts: Product[]
    stumbledProducts: Product[]
}

export interface ZettleDashboardProps extends RouteComponentProps {
    auth: typeof Auth;
    getZettleProducts: IGetProducts;
    getStumbledProducts: IGetProducts;
    syncProducts: ISyncProducts;
}

export default class ZettleDashboard extends Component<ZettleDashboardProps, ZettleDashboardState> {
    constructor(props: ZettleDashboardProps) {
        super(props);
        this.state = {
            isLoading: true,
            error: "",
            stumbledProducts: [],
            zettleProducts: []
        };
    }

    componentDidMount = async (): Promise<void> => {
        const zettleProducts = await this.getZettleProducts()
        const stumbledProducts = await this.getStumbledProducts()

        const newZettleProducts = this.removeProductsFromList(zettleProducts, stumbledProducts)

        this.setState({
            isLoading: false,
            stumbledProducts: stumbledProducts,
            zettleProducts: newZettleProducts,
        })
    }

    getZettleProducts = async (): Promise<Product[]> => {
        const shopID = localStorage.getItem(Storage.ShopID);
        if (shopID == null) {
            this.setState({
                error: "Shop could not be found. Please navigate back to the shop dashboard to resync",
            });
            return []
        }

        try {
            const session = await this.props.auth.currentSession()
            const token = session.getIdToken().getJwtToken();
            const output = await this.props.getZettleProducts
                .GetProducts({
                    identityToken: token,
                    shopID: shopID!,
                })

            if (output.statusCode === 403) {
                localStorage.removeItem(Storage.ShopID);
                this.setState({
                    error: "You were forbidden to list shop products. Please navigate back to the shop dashboard.",
                });
                return []
            }

            return output.products
        } catch (err) {
            this.setState({
                error: "You were forbidden to list shop products. Please navigate back to the shop dashboard.",
            });
            return []
        }
    };

    getStumbledProducts = async () => {
        const shopID = localStorage.getItem(Storage.ShopID);
        if (shopID == null) {
            this.setState({
                error: "Shop could not be found. Please navigate back to the shop dashboard to resync",
            });
            return []
        }

        try {
            const session = await this.props.auth.currentSession()
            const token = session.getIdToken().getJwtToken();
            const output = await this.props.getStumbledProducts
                .GetProducts({
                    identityToken: token,
                    shopID: shopID!,
                })

            if (output.statusCode === 403) {
                localStorage.removeItem(Storage.ShopID);
                this.setState({
                    error: "You were forbidden to list shop products. Please navigate back to the shop dashboard.",
                });
                return []
            }

            return output.products
        } catch (err) {
            this.setState({
                error: "You were forbidden to list shop products. Please navigate back to the shop dashboard.",
            });
            return []
        }
    };

    handleImport = (products: Product[]) => {
        const shopID = localStorage.getItem(Storage.ShopID);
        if (shopID == null) {
            return this.setState({
                error: "Shop could not be found. Please navigate back to the shop dashboard to resync",
            });
        }

        this.props.auth.currentSession().then((session) => {
            const token = session.getIdToken().getJwtToken();
            this.props.syncProducts
                .SyncProducts({
                    identityToken: token,
                    shopID: shopID!,
                    products: products,
                })
                .then(async (output) => {
                    if (output.statusCode === 403) {
                        localStorage.removeItem(Storage.ShopID);
                        return this.setState({
                            error: "You were forbidden to import the products. Please navigate back to the shop dashboard.",
                        });
                    }

                    if (output.statusCode !== 200) {
                        return this.setState({
                            error: "Could not update the products at this time. Please try again later. If this problem persists please contact support@stumbled.online"
                        });
                    }

                    let { stumbledProducts, zettleProducts } = this.state
                    stumbledProducts.push(...products)

                    zettleProducts = this.removeProductsFromList(zettleProducts, products)

                    this.setState({
                        stumbledProducts: stumbledProducts,
                        zettleProducts: zettleProducts,
                    })
                })
                .catch((err) =>
                    this.setState({
                        error: err.message,
                    })
                );
        });
    }

    removeProductsFromList = (products: Product[], productsToRemove: Product[]): Product[] => {
        const productMap = new Map<string, Product>(products.map(product => [product.id!, product]))
        const idsToRemove = productsToRemove.map(product => product.id!)
        idsToRemove.forEach(id => productMap.delete(id))
        return Array.from(productMap.values())
    }

    render() {
        const zettleDashboardProps: ZettleDashboardViewProps = {
            stumbledProducts: this.state.stumbledProducts,
            zettleProducts: this.state.zettleProducts,
            onImport: this.handleImport,
        };
        return <ViewZettleDashboard {...zettleDashboardProps} />;
    }
}

export interface ZettleDashboardViewProps {
    zettleProducts: Product[]
    stumbledProducts: Product[]
    onImport: (products: Product[]) => void;
}

export const ViewZettleDashboard: FunctionComponent<ZettleDashboardViewProps> = (props) => (
    <div>
        <h1 style={{ marginBottom: "2rem" }}>Zettle</h1>
        <Row>
            <Col style={{ borderRight: "1px dashed lightgrey" }}>
                <ListProducts title="Zettle Products to Import" products={props.zettleProducts} onSubmit={(products) => props.onImport(products)} />
            </Col>
            <Col style={{ borderLeft: "1px dashed lightgrey" }}>
                <ListProducts title="Imported Stumbled Products" products={props.stumbledProducts} />
            </Col>
        </Row>
    </div>
)

export interface ListProductsProps {
    title: string
    products: Product[]
    onSubmit?: (products: Product[]) => void
}

export interface ListProductsState {
    selectedProducts: Map<string, Product>
}

class ListProducts extends Component<ListProductsProps, ListProductsState> {
    constructor(props: ListProductsProps) {
        super(props);
        this.state = {
            selectedProducts: new Map(),
        };
    }

    handleSelect(product: Product): void {
        const { selectedProducts } = this.state

        if (selectedProducts.has(product.id!)) {
            selectedProducts.delete(product.id!)
            return this.setState({
                selectedProducts: selectedProducts
            })
        }

        selectedProducts.set(product.id!, product)
        this.setState({
            selectedProducts: selectedProducts
        })
    }

    handleSubmit(): void {
        if (!this.props.onSubmit) {
            return
        }
        const { selectedProducts } = this.state
        console.log(selectedProducts)
        const products: Product[] = Array.from(selectedProducts.values())
        this.props.onSubmit(products)
    }

    render() {
        return (
            <div style={{ padding: "1rem", display: "block" }}>
                <Row>
                    <Col>
                        <h5>{this.props.title}</h5>
                    </Col>
                    {(this.props.onSubmit) && <Col style={{ textAlign: "right" }}>
                        <Button buttonvariant={variant.Primary} onClick={() => this.handleSubmit()} name="Import" />
                    </Col>}
                </Row>
                <ListGroup style={{ marginTop: "1rem", borderTop: "1px dashed lightgrey" }} variant="flush" as="li" >
                    {this.props.products.map((product) => (
                        <ListGroup.Item className="d-flex justify-content-between align-items-start" style={{ padding: "1rem", cursor: "pointer" }} active={(this.state.selectedProducts.has(product.id!))} onClick={() => this.handleSelect(product)}>
                            <div className="ms-2 me-auto">
                                <span className="fw-bold">{product.name}</span>
                            </div>
                            <span>£{product.price}</span>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            </div>
        )
    }

}